import type { AxiosInstance } from "axios";
import { createApiService, setupInterceptors } from "@cloudaccelerator-ui/react-library";
import type { ILanguage } from "@/types/languageTypes";
import { appConfigService } from "@/services/appConfigService";

/**
 * API Service
 * Uses createApiService / setupInterceptors from the shared library
 */

let apiInstance: AxiosInstance | null = null;
let initPromise: Promise<AxiosInstance> | null = null;

/**
 * Initialises the API service with a token provider.
 * Call once from a React component that has access to the auth context.
 */
function configure(getAccessToken: () => Promise<string>): void {
  initPromise = (async (): Promise<AxiosInstance> => {
    const config = await appConfigService.getAppEnvironmentData();
    const instance = createApiService({
      baseURL: config.apiBaseUrl,
      timeout: 30000,
      headers: { "Content-Type": "application/json" },
    });
    setupInterceptors(instance, getAccessToken);
    apiInstance = instance;
    return instance;
  })();
}

/**
 * Returns the configured axios instance.
 * Waits for initialisation if configure() has been called but not yet resolved.
 */
async function getInstance(): Promise<AxiosInstance> {
  if (apiInstance) {
    return apiInstance;
  }
  if (initPromise) {
    return initPromise;
  }
  throw new Error("apiService.configure() has not been called — call it before making API requests.");
}

/**
 * Get all enabled languages from Backend
 */
async function getLanguages(): Promise<ILanguage[]> {
  const instance = await getInstance();
  const response = await instance.get<ILanguage[]>("/api/languages");
  return response.data;
}

export const apiService = {
  configure,
  getInstance,
  getLanguages,
};
